import { SocketEvents } from "@/api";
import { isDevMode } from "@/constants";
import { queryClient } from "@/lib/query-client";
import { useWsStore } from "@/providers/ws-provider";
import { ApiService } from "@/services/api/api-service";
import {
  useQueryClient,
  useQuery as useQueryTanstack,
} from "@tanstack/react-query";

import React from "react";
import { z } from "zod";

const participantCountSchema = z.object({
  participantCount: z.number(),
});

type ChatRoomRecord = z.infer<typeof participantCountSchema>;

export namespace ChatRoomSocketQuery {
  const keys = {
    all: ["chat-rooms"],
    room: (channelId: string) => [...keys.all, channelId, "socket"] as const,
  } as const;

  export function setData(channelId: string, data: ChatRoomRecord) {
    return queryClient.setQueryData(keys.room(channelId), data);
  }

  export function getData(channelId: string) {
    return queryClient.getQueryData<ChatRoomRecord>(keys.room(channelId));
  }

  export function useSocketQuery(channelId: string) {
    const { isConnected } = useWsStore();

    React.useEffect(() => {
      const socket = useWsStore.getState().socket;
      if (!socket || !isConnected) {
        return;
      }

      const key = SocketEvents.CHAT_ROOM(channelId);

      socket.on(key, (payload: unknown) => {
        if (isDevMode) {
          console.log(`Chat Room Socket ${socket.id} ON `, {
            payload,
            key,
          });
        }

        const response = ApiService.parseApiResponse(
          payload,
          participantCountSchema,
        );

        if (response.status === "error") {
          return null;
        }

        ChatRoomSocketQuery.setData(channelId, response.data);
      });

      return () => {
        socket.off(key);
      };
    }, [isConnected, channelId]);
  }

  export function useQuery(channelId: string) {
    const queryClient = useQueryClient();

    return useQueryTanstack<ChatRoomRecord>({
      queryKey: keys.room(channelId),
      queryFn: async () => {
        return (
          queryClient.getQueryData<ChatRoomRecord>(keys.room(channelId)) ?? {
            participantCount: 0,
          }
        );
      },
      enabled: !!channelId,
    });
  }
}
